import React from 'react';
import ReactDOM from 'react-dom';

interface IOwnState {
    show: boolean;
    onClose?: (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => void;
    footer?: React.ReactNode;
}

type Props = IOwnState;

interface IState {
    shouldShow: boolean;
    isVisible: boolean;
    isOpen: boolean;
}

class Modal extends React.Component<Props, IState> {
    public static getDerivedStateFromProps(nextProps: IOwnState, prevState: IState): IState {
        if (nextProps.show !== prevState.isVisible) {
            if (nextProps.show) {
                return {
                    ...prevState,
                    isVisible: true,
                    shouldShow: true
                }
            } else {
                return {
                    ...prevState,
                    isOpen: false,
                    shouldShow: false
                }
            }
        }

        return prevState;
    }

    private closeTimeout!: NodeJS.Timer;
    private drawnTimeout!: NodeJS.Timer;
    private modalRoot = document.getElementById('modal-root');
    private element = document.createElement('div');

    constructor(props: Props) {
        super(props);

        this.state = {
            isOpen: false,
            isVisible: this.props.show,
            shouldShow: this.props.show
        }
    }

    public componentDidMount() {
        this.modalRoot!.appendChild(this.element);
        this.handleShow();
        document.addEventListener('keydown', this.handleEscapeKey, false);
    }

    public componentDidUpdate() {
        this.handleToggle();
    }

    public componentWillUnmount() {
        clearTimeout(this.drawnTimeout);
        clearTimeout(this.closeTimeout);
        document.removeEventListener('keydown', this.handleEscapeKey, false);

        if (this.state.isVisible) {
            document.body.style.removeProperty('overflow');
        }

        this.modalRoot!.removeChild(this.element);
    }

    public render() {
        if (!this.state.isVisible) { return null };

        return ReactDOM.createPortal(
            <div className={`modal${this.state.isOpen ? ' is-open' : ''}`}>
                <div className="modal__container">
                    <span className="modal__container__close" onClick={this.handleToggleModal} />
                    <div className="modal__content">
                        {this.props.onClose && <span className="modal__close" onClick={this.handleToggleModal} />}
                        {this.props.children}
                    </div>
                    {this.props.footer &&
                        <div className="modal__content modal__content--theme">{this.props.footer}</div>
                    }
                </div>
            </div>, this.element
        );
    }

    private handleShow = () => {
        if (this.state.shouldShow) {
            this.drawnTimeout = setTimeout(() => {
                this.setState({
                    isOpen: true
                });

                document.body.style.overflow = 'hidden';
            }, 10);
        }
    }

    private handleToggle = () => {
        if (this.state.shouldShow) {
            if (this.state.isOpen) { return };

            clearTimeout(this.closeTimeout);
            this.handleShow();
        } else {
            if (this.state.isVisible) {
                clearTimeout(this.drawnTimeout);
                document.body.style.removeProperty('overflow');

                this.closeTimeout = setTimeout(() => {
                    this.setState({
                        isVisible: false
                    });
                }, 400);
            }
        }
    }

    private handleEscapeKey = (e: KeyboardEvent) => {
        if (this.state.isVisible && e.keyCode === 27) {
            this.onClose(e);
        }
    }

    private handleToggleModal = (e: React.FormEvent<HTMLElement>) => {
        e.persist();
        e.preventDefault();
        this.onClose(e);
    }

    private onClose(e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) {
        if (this.props.onClose) {
            this.props.onClose(e);
        }
    }
}

export default Modal;